import { useContext, useEffect, useRef, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingContext } from "../contexts/LoadingContext";
import { errorToast, successToast } from "../utils/helper-ui";
import {
  addPriceMasterProduct,
  convertToProducts,
  deleteMasterProductImage,
  deleteMasterProductPrice,
  getMasterProduct,
  getMasterProducts,
  updateMasterProduct,
} from "../services/inventory/MasterProductApi";
import { MasterProduct } from "../models/inventory/master_product";
import { Button, Carousel, IconButton, Tag } from "rsuite";
import Barcode from "react-barcode";
import { money, nl2br } from "../utils/helper";
import { uploadFile } from "../services/FileApi";
import ImageIcon from "@rsuite/icons/Image";
import { BsEye, BsTrash3 } from "react-icons/bs";
import EditIcon from "@rsuite/icons/Edit";
import MasterProductForm from "../components/features/inventory/MasterProductForm";
import PlusIcon from "@rsuite/icons/Plus";
import PriceForm from "../components/features/inventory/PriceForm";
import { ProductPrice } from "../models/inventory/product_price";
import Moment from "react-moment";
import TagIcon from "@rsuite/icons/Tag";

interface MasterProductDetailPageProps {}

const MasterProductDetailPage: FC<MasterProductDetailPageProps> = ({}) => {
  const { masterProductId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [masterProduct, setMasterProduct] = useState<MasterProduct>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [modalEdit, setModalEdit] = useState(false);

  const nav = useNavigate();
  const [modalAddPrice, setModalAddPrice] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (!masterProductId) return;
    getDetail();
  }, [masterProductId, mounted]);

  const getDetail = async () => {
    try {
      setIsLoading(true);
      let resp = await getMasterProduct(masterProductId!);
      let respJson = await resp.json();

      setMasterProduct(respJson.data);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMasterProduct = async (editedMasterProduct: MasterProduct) => {
    try {
      setIsLoading(true);
      await updateMasterProduct(masterProductId!, editedMasterProduct);
      setModalEdit(false);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeImage = async (id: string) => {
    try {
      const confirmRemoval = window.confirm(
        "Are you sure you want to remove this image?"
      );
      if (!confirmRemoval) return;
      setIsLoading(true);
      await deleteMasterProductImage(masterProductId!, id);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addPrice = async (data: ProductPrice) => {
    try {
      setIsLoading(true);
      let resp = await addPriceMasterProduct(masterProductId!, data);
      await resp.json();
      setModalAddPrice(false);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderInputFile = () => (
    <input
      type="file"
      accept="image/*"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setIsLoading(true);
          let resp = await uploadFile(
            val.target.files![0],
            {
              ref_id: masterProductId,
              ref_type: "master-product",
            },
            (progress) => {
              console.log(progress);
            }
          );
          getDetail();
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setIsLoading(false);
        }
      }}
    />
  );
  const renderImages = () => (
    <div className="w-full">
      <Carousel
        className="custom-slider"
        shape="bar"
        placement="right"
        color="orange"
      >
        {(masterProduct?.product_images ?? []).map((img, index) => (
          <div className="relative">
            <img
              key={index}
              className="object-cover"
              src={img.url}
              height="250"
            />
            <BsTrash3
              className="absolute top-2 right-2 cursor-pointer"
              onClick={() => removeImage(img.id)}
              size={16}
              color="white"
            />
          </div>
        ))}
      </Carousel>
      <IconButton
        appearance="primary"
        color="orange"
        size="sm"
        className="mt-4"
        onClick={() => {
          fileRef.current?.click();
        }}
        icon={<ImageIcon />}
      >
        Add Image
      </IconButton>
    </div>
  );
  const removePrice = async (id: string) => {
    try {
      const confirmRemoval = window.confirm(
        "Are you sure you want to remove this price?"
      );
      if (!confirmRemoval) return;

      setIsLoading(true);
      await deleteMasterProductPrice(masterProductId!, id);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <MainLayout>
      <div className="container">
        <div className="grid grid-cols-12 gap-4  h-full">
          <div className="col-span-4">
            <div className="w-full min-h-[300px] flex flex-col items-center justify-between">
              {(masterProduct?.product_images ?? []).length == 0 ? (
                <Button
                  appearance="primary"
                  color="orange"
                  onClick={() => {
                    fileRef.current?.click();
                  }}
                >
                  No Image Available
                </Button>
              ) : (
                renderImages()
              )}
            </div>
          </div>
          <div className="col-span-8">
            <div className="flex flex-row justify-between items-start">
              <div>
                {masterProduct?.category && (
                  <Tag color="green">{masterProduct?.category?.name}</Tag>
                )}
                <h1 className="text-2xl font-bold">{masterProduct?.name}</h1>
              </div>
              <div className="flex flex-row gap-2">
                <IconButton
                  size="xs"
                  appearance="primary"
                  color="blue"
                  icon={<TagIcon className="" />}
                  onClick={async () => {
                    if (
                      window.confirm(
                        `Are you sure you want to convert to product?`
                      )
                    ) {
                      try {
                        setIsLoading(true);
                        let resp = await convertToProducts([masterProductId!]);
                        let respJson = await resp.json();
                        successToast(respJson.message);
                        if (respJson.errors.length > 0) {
                          errorToast(
                            `${respJson.errors
                              .map((item: any) => `- ${item}\n`)
                              .join("")}`
                          );
                        }
                        getDetail();
                      } catch (error) {
                        errorToast(`${error}`);
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }}
                >
                  Convert To Product
                </IconButton>
                <IconButton
                  appearance="primary"
                  color="orange"
                  size="xs"
                  icon={<EditIcon />}
                  onClick={() => {
                    setModalEdit(true);
                    // Add your edit functionality here
                  }}
                >
                  Edit
                </IconButton>
              </div>
            </div>

            <div className="mb-4">
              <small className="font-bold">SKU:</small>
              <p className="text-lg"> {masterProduct?.sku}</p>
            </div>
            <div className="mb-4">
              <small className="font-bold">Barcode:</small>
              {masterProduct?.barcode && (
                <Barcode value={masterProduct?.barcode} height={30} />
              )}
            </div>
            <div className="mb-4">
              <small className="font-bold">Base Price:</small>
              <p className="text-lg"> {money(masterProduct?.price)}</p>
            </div>
            <div className="mb-4">
              <div className="flex flex-row justify-between items-center">
                <small className="font-bold">Prices:</small>
                <IconButton
                  appearance="ghost"
                  color="green"
                  size="xs"
                  className="ml-2"
                  onClick={() => {
                    setModalAddPrice(true);
                  }}
                  icon={<PlusIcon />}
                >
                  Add Price
                </IconButton>
              </div>

              {(masterProduct?.prices ?? []).length > 0 ? (
                <table className="w-full border border-gray-300 mt-4">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-xs border text-left border-gray-300">
                        Category
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Amount
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Effective Date
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Min Quantity
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {masterProduct?.prices.map((price) => (
                      <tr key={price.id}>
                        <td className="px-2 py-1 text-xs border border-gray-300">
                          {price.price_category?.name}
                        </td>
                        <td className="px-2 py-1 text-xs border text-center border-gray-300">
                          {money(price.amount)}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          <Moment format="DD/MM/YYYY">
                            {price.effective_date}
                          </Moment>
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {price.min_quantity}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          <div className="flex justify-center">
                            <BsTrash3
                              className="text-red-500 hover:text-red-800 cursor-pointer"
                              size={12}
                              onClick={() => removePrice(price.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400">No Price Available</p>
              )}
            </div>
            <div className="mb-4">
              <small className="font-bold">Description:</small>
              <div
                className="min-h-[100px] text-sm"
                dangerouslySetInnerHTML={{
                  __html: masterProduct?.description
                    ? nl2br(masterProduct?.description)
                    : "",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      {renderInputFile()}
      <MasterProductForm
        masterProduct={masterProduct}
        open={modalEdit}
        onClose={() => setModalEdit(false)}
        onSubmit={saveMasterProduct}
      />
      <PriceForm
        open={modalAddPrice}
        onClose={() => setModalAddPrice(false)}
        onSubmit={(val) => {
          val.product_id = masterProduct?.id;
          console.log(val);
          addPrice(val);
        }}
      />
    </MainLayout>
  );
};
export default MasterProductDetailPage;
